import React, { useState, useContext, useEffect } from "react";
import { functions } from "../../../../config/firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { LanguageContext } from "../../../../contexts/LanguageContext";
import { translations } from "../../../../translations";

const Contact = () => {
  const { language } = useContext(LanguageContext);
  const t = translations[language];

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmissionTime, setLastSubmissionTime] = useState(() => {
    // Check localStorage for last submission time
    const stored = localStorage.getItem("lastContactSubmission");
    return stored ? parseInt(stored) : 0;
  });
  const [timeLeft, setTimeLeft] = useState(0);

  // Add useEffect to update the countdown
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      const timeSinceLastSubmission = now - lastSubmissionTime;
      if (timeSinceLastSubmission < 300000) {
        const secondsLeft = Math.ceil(
          (300000 - timeSinceLastSubmission) / 1000
        );
        const minutesLeft = Math.ceil(secondsLeft / 60);
        setTimeLeft(minutesLeft);
      } else {
        setTimeLeft(0);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [lastSubmissionTime]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitContactForm = httpsCallable(functions, "submitContactForm");
      const result = await submitContactForm(formData);

      if (result.data.success) {
        // Store submission time
        const now = Date.now();
        localStorage.setItem("lastContactSubmission", now.toString());
        setLastSubmissionTime(now);

        setFormData({ name: "", email: "", message: "" });
        setStatus({ type: "success", message: t.contact.form.success });

        setTimeout(() => {
          setStatus({ type: "", message: "" });
        }, 2000);
      }
    } catch (error) {
      setStatus({
        type: "error",
        message: t.contact.form.error,
      });
      console.error("Error:", error);

      setTimeout(() => {
        setStatus({ type: "", message: "" });
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const getButtonText = () => {
    if (isSubmitting) return t.contact.form.sending;
    if (timeLeft > 0)
      return `Please wait ${timeLeft} minute${
        timeLeft > 1 ? "s" : ""
      } before sending another message`;
    if (status.type === "success") return t.contact.form.success;
    if (status.type === "error") return t.contact.form.error;
    return t.contact.form.submit;
  };

  const getButtonClass = () => {
    if (isSubmitting)
      return "contact-form__submit-button contact-form__submit-button--pending";
    if (timeLeft > 0)
      return "contact-form__submit-button contact-form__submit-button--waiting";
    if (status.type === "success")
      return "contact-form__submit-button contact-form__submit-button--success";
    if (status.type === "error")
      return "contact-form__submit-button contact-form__submit-button--error";
    return "contact-form__submit-button";
  };

  return (
    <section id="contact" className="contact-section">
      <h2 className="contact-section__title">{t.contact.title}</h2>
      <div className="contact-section__container">
        <div className="contact-section__info">
          <h3 className="contact-section__info-title">{t.contact.info}</h3>
          <p className="contact-section__info-item">
            📍{" "}
            {`${t.contact.address.street}, ${t.contact.address.city}, ${t.contact.address.state}`}
          </p>
          <p className="contact-section__info-item">
            📞 {t.contact.address.phone}
          </p>
          <p className="contact-section__info-item">✉️ info@socrateslab.ca</p>
        </div>
        <form onSubmit={handleSubmit} className="contact-section__form">
          <input
            type="text"
            name="name"
            placeholder={t.contact.form.name}
            value={formData.name}
            onChange={handleChange}
            required
            className="contact-section__form-input"
          />
          <input
            type="email"
            name="email"
            placeholder={t.contact.form.email}
            value={formData.email}
            onChange={handleChange}
            required
            className="contact-section__form-input"
          />
          <textarea
            name="message"
            placeholder={t.contact.form.message}
            value={formData.message}
            onChange={handleChange}
            required
            className="contact-section__form-textarea"
          />
          <button
            type="submit"
            className={getButtonClass()}
            disabled={isSubmitting || timeLeft > 0}
          >
            {getButtonText()}
          </button>
          {status.message && status.type !== "error" && (
            <p
              className={`contact-section__status-message contact-section__status-message--${status.type}`}
            >
              {status.message}
            </p>
          )}
        </form>
      </div>
    </section>
  );
};

export default Contact;
